'use client'

/**
 * ReviewMarquee — the `scroll_marquee` wall layout. A calm, horizontally
 * auto-scrolling row of compact review cards.
 *
 * The row is a NATIVE horizontal scroll container, not a CSS-translate
 * marquee. A requestAnimationFrame loop nudges `scrollLeft` at a constant
 * calm pace when idle; because it's a real scroll container, the visitor
 * can swipe (touch), drag, two-finger-scroll (trackpad), or arrow-key
 * (keyboard, once focused) to scrub through the reviews themselves. Auto-
 * advance pauses the moment they interact — touch, hover, focus, or wheel —
 * and resumes a couple seconds after they let go.
 *
 * Motion is gated off under `prefers-reduced-motion: reduce` and
 * `html[data-low-bandwidth]` (same contract as motion.css): the loop doesn't
 * run, leaving a static — but still swipeable — row. The reduced-motion gate
 * is live, so flipping the OS setting while the page is open stops/starts it.
 *
 * The scrollbar is hidden for calm; a soft right-edge mask fade stands in as
 * the "more to the side" affordance.
 *
 * The seamless-loop second copy is `aria-hidden` so screen readers don't hear
 * every review twice; it also duplicates the row so the loop can wrap
 * invisibly. The wrap period is measured from the DOM (the second copy's first
 * card `offsetLeft`), not `scrollWidth / 2` — the latter is off by half the
 * inter-card gap and would snap the row a few px every loop.
 *
 * Below the row sits a subtle "More reviews" link to the dealer's full
 * reviews page — the marquee is a teaser, the reviews page is the
 * destination. The path is handed in as `moreReviewsHref` rather than
 * hardcoded: `reviews` is a singleton page type whose base segment a tenant
 * can rename via `tenants.content_paths` (`/reviews` → `/stories`), so the
 * live path is derived per-tenant by the storefront resolver. Absent prop =
 * no link, which is also how the resolver suppresses it on the reviews page
 * itself (a "More reviews" link pointing at the page you are reading).
 *
 * SSR-safe: the markup is a plain scroll container with no JS-driven
 * attributes, so server and first client render match. The loop attaches
 * on mount.
 */

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import type { ReviewCardData } from './reviewTypes'
import { StarRating, ReviewerAvatar, formatReviewDate } from '../reviews'

/** Calm, dockside pace — roughly one 340px card every 8s. */
const SPEED_PX_PER_SEC = 42
/** How long auto-advance waits after a swipe/scroll before resuming. */
const RESUME_DELAY_MS = 2500

function MarqueeCard({ r }: { r: ReviewCardData }) {
  const dateLabel = r.review_date ? formatReviewDate(r.review_date) : ''
  return (
    <article className="w-[320px] shrink-0 bg-white rounded-2xl p-5 shadow-[var(--shadow-card-soft)]">
      <div className="flex items-center gap-3 mb-3">
        <ReviewerAvatar src={r.reviewer_avatar} name={r.reviewer_name} sizeClass="size-10 text-base" />
        <div className="min-w-0">
          <div
            className="font-bold text-[var(--color-text-primary)] truncate leading-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {r.reviewer_name}
          </div>
          <div className="flex items-center gap-1.5">
            <StarRating rating={r.rating} className="text-sm" />
            {r.review_date && dateLabel && (
              <time dateTime={r.review_date} className="text-xs text-[var(--color-text-secondary)]">
                {dateLabel}
              </time>
            )}
          </div>
        </div>
      </div>
      <p className="text-sm leading-relaxed text-[var(--color-text-primary)] line-clamp-4 m-0">
        {r.review_text}
      </p>
    </article>
  )
}

export type ReviewMarqueeProps = {
  reviews: ReviewCardData[]
  /**
   * Live path to the tenant's reviews page (honors a `content_paths`
   * rename). Omitted/null → the "More reviews" link is not rendered.
   */
  moreReviewsHref?: string | null
}

export function ReviewMarquee({ reviews, moreReviewsHref }: ReviewMarqueeProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    // Data-saver is a static, server-set attribute — honor it by never
    // starting the loop. The row stays a swipeable native scroll container.
    if (document.documentElement.hasAttribute('data-low-bandwidth')) return

    let raf = 0
    let last = 0
    let dragging = false // pointer held down on the row
    let hovering = false // mouse over the row
    let focused = false // keyboard focus within the row
    let resumeAt = 0 // timestamp before which auto-advance stays paused
    // Seamless-loop period: the x-distance from a card to its duplicate one
    // copy later. Measured from the DOM (offsetLeft of the first card of the
    // second copy) so it stays exact regardless of card width or inter-card
    // gap — `scrollWidth / 2` would be off by half a gap and snap each loop.
    let period = 0

    const measure = () => {
      const cards = el.querySelectorAll<HTMLElement>('article')
      const n = cards.length / 2
      period =
        cards.length >= 2 && Number.isInteger(n)
          ? cards[n].offsetLeft - cards[0].offsetLeft
          : 0
    }
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(el)

    const bump = () => {
      resumeAt = performance.now() + RESUME_DELAY_MS
    }
    const onPointerDown = () => {
      dragging = true
    }
    const onPointerUp = () => {
      if (dragging) {
        dragging = false
        bump()
      }
    }
    // Hover pause is mouse-only — on touch, pointerenter fires without a
    // matching leave and would wedge the row paused forever.
    const onPointerEnter = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') hovering = true
    }
    const onPointerLeave = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') hovering = false
    }
    const onFocusIn = () => {
      focused = true
    }
    const onFocusOut = () => {
      focused = false
    }
    // Trackpad / wheel scrubbing — pause briefly, then settle and resume.
    const onWheel = () => bump()

    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    el.addEventListener('pointerenter', onPointerEnter)
    el.addEventListener('pointerleave', onPointerLeave)
    el.addEventListener('focusin', onFocusIn)
    el.addEventListener('focusout', onFocusOut)
    el.addEventListener('wheel', onWheel, { passive: true })

    const tick = (now: number) => {
      if (!last) last = now
      // Clamp dt so a backgrounded tab (rAF paused) doesn't lurch the row
      // forward by hundreds of px on the first frame after refocus.
      const dt = Math.min(now - last, 50)
      last = now

      const idle = !dragging && !hovering && !focused && now >= resumeAt
      if (idle && period > 0) {
        el.scrollLeft += (SPEED_PX_PER_SEC * dt) / 1000
      }
      // Seamless wrap. Skip while a finger is down so we don't fight the drag;
      // the duplicated copy means the jump lands on visually identical content.
      if (!dragging && period > 0 && el.scrollLeft >= period) {
        el.scrollLeft -= period
      }
      raf = requestAnimationFrame(tick)
    }

    const startLoop = () => {
      if (!raf) {
        last = 0
        raf = requestAnimationFrame(tick)
      }
    }
    const stopLoop = () => {
      if (raf) {
        cancelAnimationFrame(raf)
        raf = 0
      }
    }

    // React to the OS reduced-motion setting changing while the page is open,
    // not just its value at mount. Interaction listeners stay attached either
    // way so the row remains swipeable when motion is off.
    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    const onMotionChange = () => (motionQuery?.matches ? stopLoop() : startLoop())
    motionQuery?.addEventListener?.('change', onMotionChange)
    if (!motionQuery?.matches) startLoop()

    return () => {
      stopLoop()
      ro?.disconnect()
      motionQuery?.removeEventListener?.('change', onMotionChange)
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      el.removeEventListener('pointerenter', onPointerEnter)
      el.removeEventListener('pointerleave', onPointerLeave)
      el.removeEventListener('focusin', onFocusIn)
      el.removeEventListener('focusout', onFocusOut)
      el.removeEventListener('wheel', onWheel)
    }
  }, [reviews.length])

  return (
    <div className="relative" data-component="ReviewMarquee">
      <style>{`
        .review-marquee__viewport {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          overscroll-behavior-x: contain;
          /* Soft right-edge fade — a calm "more to the side" cue now that the
             scrollbar is hidden. Last card dissolves into the section bg. */
          mask-image: linear-gradient(to right, #000 calc(100% - 64px), transparent);
          -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 64px), transparent);
        }
        .review-marquee__viewport::-webkit-scrollbar { display: none; }
        .review-marquee__track { display: flex; gap: 20px; width: max-content; }
      `}</style>

      <div
        ref={viewportRef}
        className="review-marquee__viewport"
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label="Customer reviews — swipe or scroll to browse"
      >
        <div className="review-marquee__track py-2">
          {reviews.map((r) => (
            <MarqueeCard key={r.id} r={r} />
          ))}
          {/* Seamless-loop duplicate — hidden from assistive tech. display:contents
              keeps the flex layout so the two halves are equal width. */}
          <div aria-hidden="true" style={{ display: 'contents' }}>
            {reviews.map((r) => (
              <MarqueeCard key={`dup-${r.id}`} r={r} />
            ))}
          </div>
        </div>
      </div>

      {moreReviewsHref && (
        <div className="mt-4 flex justify-center">
          <Link
            href={moreReviewsHref}
            className="inline-flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-80 px-3 py-1.5 rounded-full border"
            style={{
              // Muted, not the full band foreground: this pill sits two rows
              // above the "See all our reviews on Google" link (also muted),
              // and the two should agree on screen. Muted is also what an
              // unstyled wall (no band set) has always shown.
              color: 'var(--section-on-surface-muted, var(--color-text-secondary))',
              borderColor: 'currentColor',
            }}
          >
            More reviews
          </Link>
        </div>
      )}
    </div>
  )
}
